import { apiClient } from '@/api'
import type {
  ChatsResponse,
  MessagesResponse,
  SendTextRequest,
  SendImageRequest,
  SendDocumentRequest,
  SendWhatsAppTemplateRequest,
  WabaStatusResponse,
  WhatsAppTemplatesResponse,
} from '@/features/messenger/types/messenger.types'
import type {
  TelegramCallDetailsResponse,
  TelegramListCallsResponse,
} from '@/features/messenger/types/telegram.types'

const BASE_PATH = '/api/v1/messenger-providers/whatsapp'

export const messengerApi = {
  async getChats(params?: {
    limit?: number
    offset?: number
    search?: string
    has_media?: boolean
  }): Promise<ChatsResponse> {
    // DEV-302: курсорная пагинация (page_token — offset-строка)
    const query = {
      'pagination.pageSize': params?.limit,
      'pagination.pageToken': params?.offset ? String(params.offset) : undefined,
      search: params?.search,
      has_media: params?.has_media,
    }
    const response = await apiClient.get<ChatsResponse>(`${BASE_PATH}/chats`, { params: query })
    return response.data
  },

  async getMessages(
    chatJid: string,
    params?: { limit?: number; offset?: number; client_id?: string },
  ): Promise<MessagesResponse> {
    const encodedJid = encodeURIComponent(chatJid)
    // DEV-302: курсорная пагинация (page_token — offset-строка)
    const query = {
      'pagination.pageSize': params?.limit,
      'pagination.pageToken': params?.offset ? String(params.offset) : undefined,
      client_id: params?.client_id,
    }
    const response = await apiClient.get<MessagesResponse>(
      `${BASE_PATH}/chats/${encodedJid}/messages`,
      { params: query },
    )
    return response.data
  },

  async sendText(body: SendTextRequest): Promise<void> {
    await apiClient.post(`${BASE_PATH}/messages/text`, body)
  },

  async sendImage(body: SendImageRequest): Promise<void> {
    await apiClient.post(`${BASE_PATH}/messages/image`, body)
  },

  async sendDocument(body: SendDocumentRequest): Promise<void> {
    await apiClient.post(`${BASE_PATH}/messages/document`, body)
  },

  // DEV-1686: шаблоны WhatsApp Business — единственный способ написать вне окна 24ч.
  async listTemplates(): Promise<WhatsAppTemplatesResponse> {
    const response = await apiClient.get<WhatsAppTemplatesResponse>(`${BASE_PATH}/templates`)
    return response.data
  },

  // DEV-2566: состояние общего номера для админки «Мессенджеры» (messengers:manage).
  async getWabaStatus(): Promise<WabaStatusResponse> {
    const response = await apiClient.get<WabaStatusResponse>(
      '/api/v1/messenger-providers/waba/status',
    )
    return response.data
  },

  async sendTemplate(body: SendWhatsAppTemplateRequest): Promise<void> {
    await apiClient.post(`${BASE_PATH}/templates/send`, body)
  },

  async markAsRead(body: { phone: string; message_id: string }): Promise<void> {
    await apiClient.post(`${BASE_PATH}/messages/read`, body)
  },

  async reactMessage(body: { phone: string; message_id: string; emoji: string }): Promise<void> {
    await apiClient.post(`${BASE_PATH}/messages/react`, body)
  },

  async initiateCall(phone: string): Promise<{ callId: string; status: string }> {
    const response = await apiClient.post<{ callId: string; status: string }>(
      `${BASE_PATH}/calls/initiate`,
      { phone },
    )
    return response.data
  },

  async terminateCall(callId: string): Promise<{ status: string }> {
    const response = await apiClient.post<{ status: string }>(`${BASE_PATH}/calls/terminate`, {
      call_id: callId,
    })
    return response.data
  },

  // ---- звонки общего номера WhatsApp Business (DEV-2418) ----
  // Ответы по форме те же, что у Telegram: карточка истории общая.
  async listCalls(clientId: string): Promise<TelegramListCallsResponse> {
    const response = await apiClient.get<TelegramListCallsResponse>(`${BASE_PATH}/calls`, {
      params: { client_id: clientId },
    })
    return response.data
  },

  async getCallDetails(callId: string): Promise<TelegramCallDetailsResponse> {
    const response = await apiClient.get<TelegramCallDetailsResponse>(
      `${BASE_PATH}/calls/${encodeURIComponent(callId)}/details`,
    )
    return response.data
  },
}
